import React from 'react'

import { Grid, Typography, Paper } from '@material-ui/core'
import { Warning as WarningIcon } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useTheme } from '@material-ui/core'

import { EzosLoader } from '../../components/EzosLoader/EzosLoader'
import { Container } from '../../components'
import { InvoiceSummary } from '../../hooks/useBilling'

const useStyles = makeStyles((theme) => ({
    summaryPaper: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
    },
    placeholder: {
        color: theme.palette.text.disabled,
    },
    buttonGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    mainGrid: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        justifyContent: 'center',
    },
    topSection: {
        flex: 1,
        display: 'flex',
        flexDirection: 'row',
        maxHeight: 70,
        justifyContent: 'center',
    },
    moneySection: {
        maxWidth: 900,
        minWidth: 850,
        marginRight: theme.spacing(2),
    },
    statusSection: {
        marginLeft: theme.spacing(2),
        maxWidth: 400,
        minWidth: 300,
        marginRight: theme.spacing(2),
    },
    label: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        fontSize: 16,
    },
    value: {
        marginTop: theme.spacing(0.5),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    sendButton: {
        marginLeft: theme.spacing(2),
    },
    overdueAmount: {
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(3),
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        color: theme.palette.error.main,
    },
    warningIcon: {
        fontSize: 20,
    },
}))

interface Props {
    summaryData?: InvoiceSummary
    isLoading?: boolean
}

export const BillingSummary: React.FC<Props> = ({
    summaryData,
    isLoading = false,
}) => {
    const theme = useTheme()
    const classes = useStyles()

    const formatCurrency = (value?: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(value || 0)
    }

    const renderValue = (
        value?: number,
        isCurrency = false,
        isDays = false,
        isPercentage = false,
    ) => {
        if (!summaryData) {
            return (
                <Typography variant="h6" className={classes.placeholder}>
                    {isCurrency ? '$0.00' : '0'}
                </Typography>
            )
        }
        return (
            <Typography variant="h6" className={classes.value}>
                {isCurrency
                    ? formatCurrency(value)
                    : isDays
                    ? `${Math.round(value || 0)}`
                    : isPercentage
                    ? `${Math.round(value || 0)}%`
                    : value}
            </Typography>
        )
    }

    const calculateTotalDue = () => {
        if (!summaryData) return 0
        return summaryData.total_amount - summaryData.total_paid
    }

    const renderOverdueAmount = (value?: number) => {
        if (!summaryData) {
            return (
                <Typography variant="h6" className={classes.placeholder}>
                    $0.00
                </Typography>
            )
        }

        // If there's no overdue amount, render it normally
        if (!value || value === 0) {
            return (
                <Typography variant="h6" className={classes.value}>
                    {formatCurrency(value)}
                </Typography>
            )
        }

        // Only show warning styling if there's an actual overdue amount
        return (
            <div className={classes.overdueAmount}>
                <WarningIcon className={classes.warningIcon} />
                <Typography variant="h6" className={classes.value}>
                    {formatCurrency(value)}
                </Typography>
            </div>
        )
    }

    return (
        <Paper className={classes.summaryPaper}>
            {isLoading ? (
                <EzosLoader />
            ) : (
                <Container className={classes.mainGrid}>
                    {/* Top Section with Money and Status Counts */}
                    <Container className={classes.topSection}>
                        <Container className={classes.moneySection}>
                            <Grid container spacing={2}>
                                <Grid
                                    item
                                    xs
                                    style={{ flexGrow: 1, flexBasis: 0 }}
                                >
                                    <Typography className={classes.label}>
                                        Total Billed
                                    </Typography>

                                    {renderValue(
                                        summaryData?.total_amount,
                                        true,
                                    )}
                                </Grid>
                                <Grid
                                    item
                                    xs
                                    style={{ flexGrow: 1, flexBasis: 0 }}
                                >
                                    <Typography className={classes.label}>
                                        Total Paid
                                    </Typography>
                                    {renderValue(summaryData?.total_paid, true)}
                                </Grid>
                                <Grid
                                    item
                                    xs
                                    style={{ flexGrow: 1, flexBasis: 0 }}
                                >
                                    <Typography className={classes.label}>
                                        Total Due
                                    </Typography>
                                    {renderValue(calculateTotalDue(), true)}
                                </Grid>
                                <Grid
                                    item
                                    xs
                                    style={{ flexGrow: 1, flexBasis: 0 }}
                                >
                                    <Typography className={classes.label}>
                                        Total Overdue
                                    </Typography>
                                    {renderOverdueAmount(
                                        summaryData?.total_overdue,
                                    )}
                                </Grid>
                            </Grid>
                        </Container>
                    </Container>
                </Container>
            )}
        </Paper>
    )
}
